/**
 * File Helper Utility for handling file downloads (PDF, DOCX, etc.)
 */

/**
 * Returns formatted date string in India timezone for dossier file names
 * @param {string} format - "short" (e.g. 18-Aug-2026) or "long" (e.g. August-18-2026)
 * @returns {string} Formatted date string
 */
export const getFormattedDate = (format = "short") => {
  const today = new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
  const date = new Date(today);
  const options =
    format === "long"
      ? { month: "long", day: "2-digit", year: "numeric" }
      : { month: "short", day: "2-digit", year: "numeric" };

  return date
    .toLocaleDateString("en-US", options)
    .replace(" ", "-")
    .replace(", ", "-");
};

/**
 * Generates a clean dossier file name based on form parameters
 * @param {Object} params
 * @param {string} params.country
 * @param {string} params.productName
 * @param {string} [params.customName]
 * @param {string} [params.extension] - "pdf" | "docx"
 * @returns {string} Clean formatted file name
 */
export const generateDossierFileName = ({
  country = "Dossier",
  productName = "Product",
  customName = "",
  extension = "pdf",
}) => {
  if (customName) {
    return customName.endsWith(`.${extension}`) ? customName : `${customName}.${extension}`;
  }
  const formattedDate = getFormattedDate(extension === "pdf" ? "long" : "short");
  return `${country}_${productName}_${formattedDate}.${extension}`;
};

/**
 * Downloads a Blob or ArrayBuffer file to user's device
 * @param {Blob|ArrayBuffer|string} fileData - The file content or Blob/Object URL
 * @param {string} fileName - The desired name for the downloaded file
 * @param {string} [mimeType] - Optional MIME type if creating a new Blob
 */
export const downloadFile = (fileData, fileName, mimeType = "application/octet-stream") => {
  if (!fileData) return;

  let fileUrl = "";
  let isCreatedUrl = false;

  if (typeof fileData === "string" && fileData.startsWith("blob:")) {
    fileUrl = fileData;
  } else {
    const blob = fileData instanceof Blob ? fileData : new Blob([fileData], { type: mimeType });
    fileUrl = URL.createObjectURL(blob);
    isCreatedUrl = true;
  }

  const anchor = document.createElement("a");
  anchor.href = fileUrl;
  anchor.download = fileName;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();

  setTimeout(() => {
    document.body.removeChild(anchor);
    if (isCreatedUrl) {
      URL.revokeObjectURL(fileUrl);
    }
  }, 100);
};

/**
 * Downloads a PDF file
 * @param {Blob|ArrayBuffer|string} data - PDF data or Blob URL
 * @param {string} fileName - Target file name
 */
export const downloadPdf = (data, fileName) => {
  downloadFile(data, fileName, "application/pdf");
};

/**
 * Downloads a DOCX Word file
 * @param {Blob|ArrayBuffer|string} data - DOCX data
 * @param {string} fileName - Target file name
 */
export const downloadDocx = (data, fileName) => {
  downloadFile(
    data,
    fileName,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  );
};
